import {
  PromoBanner,
  RewardsBanner,
  LoyaltyInterruptModal,
  LargeOrderPromo,
  LargeOrderPromoCard,
  JoinRewards,
  LegalCopy,
  UpsellItem,
  GuacMode,
  SystemFlags,
  FeaturedPCE,
  OrderWebMetadata,
  OrderWebTitleDescription,
  PickupModality,
  CreateAccountSignInContentFragment,
  OrderConfirmationCreateAccountExperience,
} from '@/models/cms';
import { CmsLink } from '@/models/cms';
import {
  getPromoBanner,
  getHeaderLinks,
  getRewardsBanner,
  getLoyaltyInterruptModal,
  getLargeOrderPromo,
  getLargeOrderCard,
  getJoinRewards,
  getLegalCopy,
  getDynamicUpsellItems,
  getSystemFlags,
  getGuacMode,
  getFeaturedPCE,
  getMetadata,
  getOrderWebTitleAndDescription,
  getNutritionPath,
  getPickupOptions,
  getCreateAccountForm,
  getSignInModal,
  getOrderConfirmationGuest,
} from './builders';
import { LogLevel } from '@/services/logger/logger';
import { getCountryCodeByTopLevelDomain } from '@/helpers';
import { CountryCode } from '@/store/ui';
import axios from 'axios'

export interface Config {
  isLocal: boolean;
  recaptcha: {
    key: string;
    accountName: string;
  };

  payeezy: {
    jsHost: string;
    apiKey: string;
    jsSecurityKey: string;
    taToken: string;
    jsLibrary: string;
  };

  chase: {
    baseFormUrl: string;
    jsLibraryUrl: string;
  };

  services: {
    gatewayUrl: string;
    gatewaySubscriptionKey: string;
    channel: string;
    refreshPointsThrottleTimeInMinutes?: number;
  };

  cms: {
    baseUrl: string;
    baseUrlCanada: string;
    headerLinks: CmsLink[];
    promoBannerTopGuestDesktop: PromoBanner | null;
    promoBannerTopGuestMobile: PromoBanner | null;
    promoBannerTopAuthenticatedDesktop: PromoBanner | null;
    promoBannerTopAuthenticatedMobile: PromoBanner | null;
    promoBannerTopGuestDesktopCanada: PromoBanner | null;
    promoBannerTopGuestMobileCanada: PromoBanner | null;
    promoBannerTopAuthenticatedDesktopCanada: PromoBanner | null;
    promoBannerTopAuthenticatedMobileCanada: PromoBanner | null;
    promoBannerBottom: PromoBanner | null;
    promoBannerBottomCanada: PromoBanner | null;
    promoBannerRewardsGuest: PromoBanner | null;
    promoBannerRewardsAuthenticatedNotEnrolled: PromoBanner | null;
    largeOrderPromo: LargeOrderPromo | null;
    guacMode: GuacMode | null;
    groupOrderCard: LargeOrderPromoCard | null;
    cateringCard: LargeOrderPromoCard | null;
    cateringCardCanada: LargeOrderPromoCard | null;
    joinRewards: JoinRewards | null;
    legalCopy: LegalCopy | null;
    homePageRewardsBanner: RewardsBanner;
    homePagePromoBannerCanada: RewardsBanner;
    rewardsPageGuacModeRewardsBanner: RewardsBanner;
    rewardsPageGuestUnenrolledRewardsBanner: RewardsBanner;
    loyaltyInterruptModal: LoyaltyInterruptModal;
    dynamicUpsellItems: UpsellItem[];
    dynamicUpsellItemsPickup?: UpsellItem[];
    dynamicUpsellItemsDelivery?: UpsellItem[];
    systemFlags: SystemFlags;
    createAccountFormFragment: CreateAccountSignInContentFragment;
    signInModalFragment: CreateAccountSignInContentFragment;
    featuredPCE: FeaturedPCE;
    metadata: OrderWebMetadata | null;
    titleAndDescription: OrderWebTitleDescription | null;
    fullNutritionURL: string | undefined;
    pickupModality: PickupModality | undefined;
    orderConfirmationCreateAccountExperience: OrderConfirmationCreateAccountExperience | null;
  };

  urls: {
    buyGiftCards: string;
    support: string;
    catering: string;
    faq: string;
    sessionFrameUrl: string;
    realFoodprint: string;
    howGood: string;
    rewardsPath: string;
    twitterAchievementBaseUrl: string;
    allergenStatementUrl: string;
  };

  logging: {
    level: LogLevel;
    shouldVerifyFields: boolean;
    maxFieldsLength?: number;
  };

  google: {
    mapsUrl: string;
    mapsAuth: string;
    analyticsId: string;
  };

  adobe: {
    analyticsUrl: string;
  },

  build: string;

  isStubPayment: boolean;
  stubPaymentInformation: {
    cardHolderName: string;
    chipotleWalletId: number | undefined;
    creditCardExpiration: string;
    creditCardSingleUseToken: string;
    creditCardType: string;
    creditCardZipCode: string;
    lastFourAccountNumbers: string;
    paymentProviderId: string;
    paymentProviderId_CA: string;
    paymentType: string;
  }

}

export enum AEMVariables {
  promoBannerTopAuthenticatedDesktop = '_cmgPromoTopGuestDesktop',
  promoBannerTopAuthenticatedMobile = '_cmgPromoTopGuestMobile',
  promoBannerTopGuestDesktop = '_cmgPromoTopGuestDesktop',
  promoBannerTopGuestMobile = '_cmgPromoTopGuestMobile',
  promoBannerTopAuthenticatedDesktopCanada = '_cmgPromoTopGuestDesktopCanada',
  promoBannerTopAuthenticatedMobileCanada = '_cmgPromoTopGuestMobileCanada',
  promoBannerTopGuestDesktopCanada = '_cmgPromoTopGuestDesktopCanada',
  promoBannerTopGuestMobileCanada = '_cmgPromoTopGuestMobileCanada',
  promoBannerBottom = '_cmgPromoBottom',
  promoBannerBottomCanada = '_cmgPromoBottomCanada',
  promoBannerRewardsGuest = '_cmgPromoRewardsHeroGuest',
  promoBannerRewardsAuthenticatedNotEnrolled = '_cmgPromoRewardsHeroAuthenticatedNotEnrolled',
  largeOrderPromo = '_cmgLargeOrderPromo',
  guacMode = '_cmgGuacMode',
  groupOrderCard = '_cmgGroupOrderCard',
  cateringCard = '_cmgCateringCard',
  cateringCardCanada = '_cmgCateringCardCanada',
  joinRewards = '_cmgJoinRewards',
  legalCopy = '_cmgLegalCopy',
  homePageRewardsBanner = '_cmgHomePageRewardsBanner',
  homePagePromoBannerCanada = '_cmgHomePagePromoBannerCanada',
  rewardsPageGuacModeRewardsBanner = '_cmgRewardsPageGuacModeRewardsBanner',
  rewardsPageGuestUnenrolledRewardsBanner = '_cmgRewardsPageGuestUnenrolledRewardsBanner',
  loyaltyInterruptModal = '_cmgLoyaltyInterruptModal',
  dynamicUpsellItems = '_cmgDynamicUpsell',
  systemFlags = '_cmgSystemFlags',
  featuredPCE = '_cmgFeaturedPCE',
  metadata = '_cmgTitleAndDescription',
  createAccountFormFragment = '_cmgCreateAccountForm',
  signInModalFragment = '_cmgSignInModal',
  titleAndDescription = '_cmgTitleAndDescription',
  fullNutritionURL = '_cmgNutritionPdf',
  fullNutritionURLCanada = '_cmgNutritionPdfCanada',
  pickupModality = '_cmgPickupOptions',
  orderConfirmationCreateAccountExperience = '_cmgOrderConfirmationGuest'
}

export async function buildConfig(): Promise<Config> {
  let upsellItems: any = [];
  let legalText: any = {};
  let loyaltyInterrupt: any = {};
  let createAccountForm: any = {};
  let signInModal: any = {};
  let csJoinRewards: any = {};
  let pickupOptions: any = [];
  const isCanada = getCountryCodeByTopLevelDomain() == CountryCode.CA;
  const cmsBaseUrl = str(process.env.VUE_APP_CMS_BASEURL);
  const cmsBaseUrlCanada = str(process.env.VUE_APP_CMS_BASEURL_CANADA);
  const isHomepageOnAEM = window._cmgIsAEMCS ?? false;
  const gqlUrl = str(process.env.VUE_APP_GQL);
  const isLocal = bool(process.env.VUE_APP_IS_LOCAL);
  const cateringUrl = isCanada ? str(process.env.VUE_APP_URL_CATERING_CANADA) : str(process.env.VUE_APP_URL_CATERING);  
  let indexedCmsVars: { [index: string] : Object } = {};

  // If we're running locally, we need to fetch the AEM content from a lower env
  if (isLocal) {
    indexedCmsVars = await getIndexedCmsVarsForLocalInstance(cmsBaseUrl);
  }

  //process GQL queries here if we're in AEMCS
  if(isHomepageOnAEM){
    if(isCanada){
      const canadaURL = gqlUrl.replace('.com', '.ca')
      console.log('in Canada')
      upsellItems = await grabUpsellItemsFromGraphQL(`${canadaURL}/upsell`)
      legalText = await grabLegalTextFromGraphQL(`${canadaURL}/legal-text;region=en-ca`)
      pickupOptions = await grabPickupOptionsFromGraphQL(`${canadaURL}/pickup-options;region=en-ca`)
      loyaltyInterrupt = await grabLoyaltyInterruptFromGraphQL(`${canadaURL}/loyalty-interrupt-modal`)
      createAccountForm = await grabCreateAccountModalFromGraphQL(`${canadaURL}/create-account-modal;region=en-ca`)
      signInModal = await grabSignInModalFromGraphQL(`${canadaURL}/sign-in-modala;region=en-ca`)
      csJoinRewards = await grabJoinRewardsFromGraphQL(`${canadaURL}/join-rewards`)
    } else {
      console.log('in US')
      upsellItems = await grabUpsellItemsFromGraphQL(`${gqlUrl}/upsell`)
      legalText = await grabLegalTextFromGraphQL(`${gqlUrl}/legal-text;region=en-us`)
      pickupOptions = await grabPickupOptionsFromGraphQL(`${gqlUrl}/pickup-options;region=en-us`)
      loyaltyInterrupt = await grabLoyaltyInterruptFromGraphQL(`${gqlUrl}/loyalty-interrupt-modal`)
      createAccountForm = await grabCreateAccountModalFromGraphQL(`${gqlUrl}/create-account-modal;region=en-us`)
      signInModal = await grabSignInModalFromGraphQL(`${gqlUrl}/sign-in-modal;region=en-us`)
      csJoinRewards = await grabJoinRewardsFromGraphQL(`${gqlUrl}/join-rewards`)
    }
  }

  return {
    isLocal,
    recaptcha: {
      key: str(process.env.VUE_APP_RECAPTCHA_KEY),
      accountName: str(process.env.VUE_APP_RECAPTCHA_ACCOUNT_NAME),
    },
    payeezy: {
      jsHost: str(process.env.VUE_APP_PAYEEZY_JS_HOST),
      apiKey: str(process.env.VUE_APP_PAYEEZY_API_KEY),
      jsSecurityKey: str(process.env.VUE_APP_PAYEEZY_JS_SECURITY_KEY),
      taToken: str(process.env.VUE_APP_PAYEEZY_TA_TOKEN),
      jsLibrary: str(process.env.VUE_APP_PAYEEZY_JS_LIBRARY),
    },
    chase: {
      baseFormUrl: str(process.env.VUE_APP_CHASE_BASE_URL),
      jsLibraryUrl: str(process.env.VUE_APP_CHASE_JS_LIBRARY),
    },
    services: {
      gatewayUrl: str(process.env.VUE_APP_SERVICES_GATEWAY_URL),
      gatewaySubscriptionKey: str(process.env.VUE_APP_SERVICES_GATEWAY_SUBSCRIPTION_KEY),
      channel: str(process.env.VUE_APP_SERVICES_CHANNEL),
      refreshPointsThrottleTimeInMinutes: num(process.env.VUE_APP_SERVICES_REFRESH_POINTS_THROTTLE_TIME_IN_MINUTES),
    },
    cms: {
      baseUrl: cmsBaseUrl,
      baseUrlCanada: cmsBaseUrlCanada,
      // Product requested we remove the distinction between auth and guest for the hero banner. Changing it here so we can revert on request.
      // If the revert hasnt happened in a few months we should clean it up properly. Also see cms store.
      // promoBannerTopAuthenticatedDesktop: getPromoBanner(cmsBaseUrl, window._cmgPromoTopAuthenticatedDesktop),
      // promoBannerTopAuthenticatedMobile: getPromoBanner(cmsBaseUrl, window._cmgPromoTopAuthenticatedMobile),
      // promoBannerTopAuthenticatedDesktopCanada: getPromoBanner(cmsBaseUrl, window._cmgPromoTopAuthenticatedDesktopCanada),
      // promoBannerTopAuthenticatedMobileCanada: getPromoBanner(cmsBaseUrl, window._cmgPromoTopAuthenticatedMobileCanada),
      promoBannerTopAuthenticatedDesktop: getPromoBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerTopAuthenticatedDesktop] as AemPromoBanner : window[AEMVariables.promoBannerTopAuthenticatedDesktop]),
      promoBannerTopAuthenticatedMobile: getPromoBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerTopAuthenticatedMobile] as AemPromoBanner : window[AEMVariables.promoBannerTopAuthenticatedMobile]),
      promoBannerTopGuestDesktop: getPromoBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerTopGuestDesktop] as AemPromoBanner : window[AEMVariables.promoBannerTopGuestDesktop]),
      promoBannerTopGuestMobile: getPromoBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerTopGuestMobile] as AemPromoBanner : window[AEMVariables.promoBannerTopGuestMobile]),
      promoBannerTopAuthenticatedDesktopCanada: getPromoBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerTopAuthenticatedDesktopCanada] as AemPromoBanner : window[AEMVariables.promoBannerTopAuthenticatedDesktopCanada]),
      promoBannerTopAuthenticatedMobileCanada: getPromoBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerTopAuthenticatedMobileCanada] as AemPromoBanner : window[AEMVariables.promoBannerTopAuthenticatedMobileCanada]),
      promoBannerTopGuestDesktopCanada: getPromoBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerTopGuestDesktopCanada] as AemPromoBanner : window[AEMVariables.promoBannerTopGuestDesktopCanada]),
      promoBannerTopGuestMobileCanada: getPromoBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerTopGuestMobileCanada] as AemPromoBanner : window[AEMVariables.promoBannerTopGuestMobileCanada]),
      promoBannerBottom: getPromoBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerBottom] as AemPromoBanner : window[AEMVariables.promoBannerBottom]),
      promoBannerBottomCanada: getPromoBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerBottomCanada] as AemPromoBanner : window[AEMVariables.promoBannerBottomCanada]),
      promoBannerRewardsGuest: getPromoBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerRewardsGuest] as AemPromoBanner : window[AEMVariables.promoBannerRewardsGuest]),
      promoBannerRewardsAuthenticatedNotEnrolled: getPromoBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerRewardsAuthenticatedNotEnrolled] as AemPromoBanner : window[AEMVariables.promoBannerRewardsAuthenticatedNotEnrolled]),
      headerLinks: getHeaderLinks(cateringUrl),
      largeOrderPromo: getLargeOrderPromo(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.largeOrderPromo] as AemLargeOrderPromo : window[AEMVariables.largeOrderPromo]),
      guacMode: getGuacMode(isLocal ? indexedCmsVars[AEMVariables.guacMode] as AemGuacMode : window[AEMVariables.guacMode]),
      groupOrderCard: getLargeOrderCard(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.groupOrderCard] as AemLargeOrderCard : window[AEMVariables.groupOrderCard]),
      cateringCard: getLargeOrderCard(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.cateringCard] as AemLargeOrderCard : window[AEMVariables.cateringCard]),
      cateringCardCanada: getLargeOrderCard(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.cateringCardCanada] as AemLargeOrderCard : window[AEMVariables.cateringCardCanada]),
      joinRewards: !isHomepageOnAEM ? getJoinRewards(isLocal ? indexedCmsVars[AEMVariables.joinRewards] as AemJoinRewards : window[AEMVariables.joinRewards]) : getJoinRewards(isLocal ? indexedCmsVars[AEMVariables.joinRewards] as AemJoinRewards : window[AEMVariables.joinRewards], csJoinRewards),
      legalCopy: !isHomepageOnAEM ? getLegalCopy(isLocal ? indexedCmsVars[AEMVariables.legalCopy] as AemLegalCopy : window[AEMVariables.legalCopy]) : getLegalCopy(isLocal ? indexedCmsVars[AEMVariables.legalCopy] as AemLegalCopy : window[AEMVariables.legalCopy], legalText),
      homePageRewardsBanner: getRewardsBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.homePageRewardsBanner] as AemRewardsBanner : window[AEMVariables.homePageRewardsBanner]),
      homePagePromoBannerCanada: getRewardsBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.homePagePromoBannerCanada] as AemRewardsBanner : window[AEMVariables.homePagePromoBannerCanada]),
      rewardsPageGuacModeRewardsBanner: getRewardsBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.rewardsPageGuacModeRewardsBanner] as AemRewardsBanner : window[AEMVariables.rewardsPageGuacModeRewardsBanner]),
      rewardsPageGuestUnenrolledRewardsBanner: getRewardsBanner(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.rewardsPageGuestUnenrolledRewardsBanner] as AemRewardsBanner : window[AEMVariables.rewardsPageGuestUnenrolledRewardsBanner]),
      loyaltyInterruptModal: !isHomepageOnAEM ? getLoyaltyInterruptModal(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.loyaltyInterruptModal] as AemLoyaltyInterruptModal : window[AEMVariables.loyaltyInterruptModal]) : getLoyaltyInterruptModal(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.loyaltyInterruptModal] as AemLoyaltyInterruptModal : window[AEMVariables.loyaltyInterruptModal], loyaltyInterrupt),
      dynamicUpsellItems: !isHomepageOnAEM ? getDynamicUpsellItems(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.dynamicUpsellItems] as AemJSON : window[AEMVariables.dynamicUpsellItems]) :  getDynamicUpsellItems(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.dynamicUpsellItems] as AemJSON : window[AEMVariables.dynamicUpsellItems], upsellItems),
      systemFlags: getSystemFlags(isLocal ? indexedCmsVars[AEMVariables.systemFlags] as AemJSON : window[AEMVariables.systemFlags]),
      featuredPCE: getFeaturedPCE(isLocal ? indexedCmsVars[AEMVariables.featuredPCE] as AemFeaturedPCE : window[AEMVariables.featuredPCE]),
      metadata: getMetadata(isLocal ? indexedCmsVars[AEMVariables.metadata] as AemMetadata : window[AEMVariables.metadata]),
      createAccountFormFragment: !isHomepageOnAEM ? getCreateAccountForm(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.createAccountFormFragment] as AemAccountContentFragment : window[AEMVariables.createAccountFormFragment]) : getCreateAccountForm(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.createAccountFormFragment] as AemAccountContentFragment : window[AEMVariables.createAccountFormFragment], createAccountForm),
      signInModalFragment: !isHomepageOnAEM ? getSignInModal(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.signInModalFragment] as AemAccountContentFragment : window[AEMVariables.signInModalFragment]) : getSignInModal(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.signInModalFragment] as AemAccountContentFragment : window[AEMVariables.signInModalFragment], signInModal),
      titleAndDescription: getOrderWebTitleAndDescription(isLocal ? indexedCmsVars[AEMVariables.titleAndDescription] as AemMetadata : window[AEMVariables.titleAndDescription]),
      fullNutritionURL: getNutritionPath(isCanada ? (isLocal ? indexedCmsVars[AEMVariables.fullNutritionURLCanada] as AemNutritionPath : window[AEMVariables.fullNutritionURLCanada]) : (isLocal ? indexedCmsVars[AEMVariables.fullNutritionURL] as AemNutritionPath : window[AEMVariables.fullNutritionURL])),
      pickupModality: !isHomepageOnAEM ? getPickupOptions(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.pickupModality] as AemPickupOptions : window[AEMVariables.pickupModality]) : getPickupOptions(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.pickupModality] as AemPickupOptions : window[AEMVariables.pickupModality], pickupOptions),
      orderConfirmationCreateAccountExperience: getOrderConfirmationGuest(cmsBaseUrl, isLocal ? indexedCmsVars[AEMVariables.promoBannerRewardsAuthenticatedNotEnrolled] as AEMGuestOrderCreateAccountFragment : window[AEMVariables.orderConfirmationCreateAccountExperience]) 
    },
    urls: {
      buyGiftCards: str(process.env.VUE_APP_URL_BUY_GIFT_CARDS),
      support: isCanada ? str(process.env.VUE_APP_URL_SUPPORT_CANADA) : str(process.env.VUE_APP_URL_SUPPORT),
      catering: cateringUrl,
      faq: str(process.env.VUE_APP_URL_FAQ),
      sessionFrameUrl: str(process.env.VUE_APP_SESSION_FRAME_URL),
      realFoodprint: str(process.env.VUE_APP_REAL_FOODPRINT_URL),
      howGood: str(process.env.VUE_APP_HOW_GOOD_URL),
      rewardsPath: str(process.env.VUE_APP_REWARDS_PATH),
      twitterAchievementBaseUrl: str(process.env.VUE_APP_TWITTER_ACHIEVEMENT_BASE_URL),
      allergenStatementUrl: str(process.env.VUE_APP_ALLERGEN_STATEMENT_URL),
    },
    logging: {
      level: (LogLevel[str(process.env.VUE_APP_LOGGING_LEVEL) as any] as any) as LogLevel,
      shouldVerifyFields: isLocal,
      maxFieldsLength: num(process.env.VUE_APP_LOGGING_MAX_FIELDS_LENGTH),
    },
    google: {
      mapsUrl: str(process.env.VUE_APP_GOOGLE_MAPS_URL),
      mapsAuth: str(process.env.VUE_APP_GOOGLE_MAPS_AUTH),
      analyticsId: str(process.env.VUE_APP_GOOGLE_ANALYTICS_ID),
    },
    adobe: {
      analyticsUrl: str(process.env.VUE_APP_ADOBE_ANALYTICS_URL),
    },
    build: str(process.env.VUE_APP_BUILD),
    isStubPayment: bool(process.env.VUE_APP_IS_STUB_PAYMENT),
    stubPaymentInformation:{
      cardHolderName: str(process.env.VUE_APP_STUB_PAYMENT_CARD_HOLDER_NAME),
      chipotleWalletId: num(process.env.VUE_APP_STUB_PAYMENT_CHIPOTLE_WALLET_ID),
      creditCardExpiration: str(process.env.VUE_APP_STUB_PAYMENT_CREDIT_CARD_EXPIRATION),
      creditCardSingleUseToken: str(process.env.VUE_APP_STUB_PAYMENT_CREDIT_CARD_SINGLE_USE_TOKEN),
      creditCardType: str(process.env.VUE_APP_STUB_PAYMENT_CREDIT_CARD_TYPE),
      creditCardZipCode: str(process.env.VUE_APP_STUB_PAYMENT_CREDIT_CARD_ZIP_CODE),
      lastFourAccountNumbers: str(process.env.VUE_APP_STUB_PAYMENT_LAST_FOUR_ACCOUNT_NUMBERS),
      paymentProviderId: str(process.env.VUE_APP_STUB_PAYMENT_PAYMENT_PROVIDER_ID),
      paymentProviderId_CA: str(process.env.VUE_APP_STUB_PAYMENT_PAYMENT_PROVIDER_ID_CA),
      paymentType: str(process.env.VUE_APP_STUB_PAYMENT_PAYMENT_TYPE)
    }
  };
}

function str(val: any): string {
  return val as string;
}

function bool(val: any): boolean {
  return (val && val.toLowerCase && val.toLowerCase() === 'true') || false;
}

function num(val: any): number | undefined {
  if (val === undefined) {
    return;
  }

  return parseFloat(val);
}

// This function looks at one of the lower environments (usually INT) and pulls the AEM content fragments from the DOM
// Then based on what we know to be the standard format of these conent fragment variables, we parse them out and inject them into the app
// This is pretty gross string parsing code, but this is only for the app to work properly when running locally, so I think we can live with it
// Additionally, by doing it this way, we are no longer injecting content into the app via a script tag, which was causing an XSS vulnerability
async function getIndexedCmsVarsForLocalInstance(cmsBaseUrl: string) {
  const content = await fetch(cmsBaseUrl).then(r => r.text());
  const startTag = `<!-- JSON renderer content fragment -->`;
  const endParseTag = '<div id="order-web-scripts-and-styles">';
  const subContent = content.substring(content.indexOf(startTag) + startTag.length, content.indexOf(endParseTag));
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(subContent, 'text/html');
  const cmsVariables: { [index: string]: Object } = {};

  for (const script of htmlDoc.scripts) {
    let splitText = script.text.split('var ')[1].split(' = '); 
    let cmsVariableName = splitText[0];
    let valueStart = splitText[1].indexOf('{');
    let valueEnd = splitText[1].lastIndexOf('}');
    let value = splitText[1].substring(valueStart, valueEnd + 1);

    cmsVariables[cmsVariableName] = JSON.parse(value);
  }

  return cmsVariables;
}

/******************************GRAPHQL QUERIES**************************************/
//TODO clean this up to avoid code repetition

async function grabUpsellItemsFromGraphQL(graphQLUrl: string) {
  return await axios.get(graphQLUrl)
  .then((res)=>{
    if(res.data.data.upsellList.items){
      console.log('upsell query', res.data.data.upsellList.items)
      return res.data.data.upsellList.items;
    }
  }).catch((err) => {
    console.log('error fetching upsell', err)
    return [];
  })
}

async function grabLegalTextFromGraphQL(graphQLUrl: string) {
  return await axios.get(graphQLUrl)
  .then((res)=>{
    if(res.data.data.legalTextList){
      console.log('legal text query', res.data.data.legalTextList.items[0])
      return res.data.data.legalTextList.items[0];
    }
  }).catch((err) => {
    console.log('error fetching legal text', err)
    return [];
  })
}

async function grabPickupOptionsFromGraphQL(graphQLUrl: string) {
  return await axios.get(graphQLUrl)
  .then((res)=>{
    if(res.data.data.pickupOptionList){
      console.log('pickup options query', res.data.data.pickupOptionList.items)
      return res.data.data.pickupOptionList.items;
    }
  }).catch((err) => {
    console.log('error fetching pickup options', err)
    return [];
  })
}

async function grabLoyaltyInterruptFromGraphQL(graphQLUrl: string) {
  return await axios.get(graphQLUrl)
  .then((res)=>{
    if(res.data.data.loyaltyInterruptModalList){
      console.log('loyalty interrupt query', res.data.data.loyaltyInterruptModalList.items[0])
      return res.data.data.loyaltyInterruptModalList.items[0];
    }
  }).catch((err) => {
    console.log('error fetching loyalty interrupt', err)
    return [];
  })
}

async function grabCreateAccountModalFromGraphQL(graphQLUrl: string) {
  return await axios.get(graphQLUrl)
  .then((res)=>{
    if(res.data.data.createAccountModalList){
      console.log('create account query', res.data.data.createAccountModalList.items[0])
      return res.data.data.createAccountModalList.items[0];
    }
  }).catch((err) => {
    console.log('error fetching create account', err)
    return [];
  })
}

async function grabSignInModalFromGraphQL(graphQLUrl: string) {
  return await axios.get(graphQLUrl)
  .then((res)=>{
    if(res.data.data.signInModalList){
      console.log('sign in modal query', res.data.data.signInModalList.items[0])
      return res.data.data.signInModalList.items[0];
    }
  }).catch((err) => {
    console.log('error fetching sign in modal', err)
    return [];
  })
}

async function grabJoinRewardsFromGraphQL(graphQLUrl: string) {
  return await axios.get(graphQLUrl)
  .then((res)=>{
    if(res.data.data.joinRewardsList){
      console.log('join rewards query', res.data.data.joinRewardsList.items[0])
      return res.data.data.joinRewardsList.items[0];
    }
  }).catch((err) => {
    console.log('error fetching join rewards', err)
    return [];
  })
}
